import type { ChangeEvent } from 'react';
import type { ControllerRenderProps, FieldPath, FieldValues } from 'react-hook-form';
import { FormFieldWrapper, type FormFieldWrapperProps } from '../components/FormFieldWrapper';
import { Input } from './Input';

/**
 * InputField コンポーネント
 *
 * FieldItem でラップされた Input コンポーネント
 * React Hook Form と統合され、ラベル・バリデーション・エラーメッセージを含む
 *
 * 使用例:
 * ```tsx
 * <InputField
 *   control={form.control}
 *   name="email"
 *   label="メールアドレス"
 *   type="email"
 *   placeholder="email@example.com"
 *   required
 * />
 * ```
 */
export interface InputFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<FormFieldWrapperProps<TFieldValues, TName>, 'children'>,
    Omit<React.ComponentProps<'input'>, 'name' | 'size' | 'autoComplete'> {
  /**
   * カンマ区切りで数値を表示するか
   * type="number"の場合に有効
   */
  useCommaFormat?: boolean;
  /**
   * 入力フィールドの右側に表示する接尾辞（例: "千円", "円", "km"）
   */
  suffix?: React.ReactNode;
  /**
   * クリアボタンを表示するか（値が入力されている場合のみ表示）
   */
  showAllClear?: boolean;
  /// 自動補完の設定（true: "on", false: "off", 文字列: そのまま渡す）
  autoComplete: boolean | string;
}

export const InputField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  required,
  supportText,
  showSupportText,
  readOnly,
  displayValue,
  className,
  type = 'text',
  placeholder,
  size = 'sm',
  suffix,
  showAllClear = false,
  autoComplete,
  ...inputProps
}: InputFieldProps<TFieldValues, TName>) => {
  const { onChange: inputOnChange, ...restInputProps } = inputProps;

  return (
    <FormFieldWrapper
      control={control}
      name={name}
      label={label}
      required={required}
      supportText={supportText}
      showSupportText={showSupportText}
      readOnly={readOnly}
      displayValue={displayValue}
      className={className}
      size={size}
    >
      {({
        field,
        fieldId,
        hasError,
      }: {
        field: ControllerRenderProps<TFieldValues, TName>;
        fieldId: string;
        hasError: boolean;
      }) => (
        <Input
          {...field}
          {...restInputProps}
          id={fieldId}
          type={type}
          placeholder={placeholder}
          suffix={suffix}
          showAllClear={showAllClear}
          autoComplete={autoComplete}
          aria-invalid={hasError}
          aria-describedby={hasError ? `${fieldId}-error` : undefined}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            inputOnChange?.(event);

            if (type === 'number') {
              const rawValue = event.target.value;

              if (rawValue === '') {
                field.onChange(undefined);
                return;
              }

              const numericValue = Number(rawValue);
              if (!Number.isNaN(numericValue)) {
                field.onChange(numericValue);
              }
              return;
            }

            field.onChange(event);
          }}
        />
      )}
    </FormFieldWrapper>
  );
};

InputField.displayName = 'InputField';
